import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  BIND_VERIFICATION_SERVICES,
  GET_VERIFICATION_SERVICES,
  GET_VERIFICATION_SERVICE_BINDINGS,
  REMOVE_VERIFICATION_BINDING,
  get_request_config,
} from '@/utils/system_constants/constant';

export default {
  name: 'BindServices',
  mixins: [alert_mixin],
  props: {
    entityTypeId: {
      type: Number,
      required: false,
      default: null,
    },
    entityTypeName: {
      type: String,
      required: false,
      default: '',
    },
    entityConfigId: {
      type: Number,
      required: false,
      default: null,
    },
  },
  data: () => ({
    services: [],
    service_bindings: [],
    selected_service_id: '',
    creating_service_binding: false,
    removing_binding_id: null,
  }),
  computed: {
    available_services_count() {
      return Array.isArray(this.services) ? this.services.length : 0;
    },
    bound_services_count() {
      return Array.isArray(this.service_bindings) ? this.service_bindings.length : 0;
    },
    bindable_services() {
      const bound_service_ids = new Set(
        (this.service_bindings || [])
          .map((binding) => Number(
            binding?.verification_service_id ??
            binding?.service_id ??
            binding?.service ??
            binding?.verification_service
          ))
          .filter((id) => Number.isFinite(id) && id > 0)
      );

      return (Array.isArray(this.services) ? this.services : [])
        .filter((service) => Number.isFinite(Number(service?.id)))
        .filter((service) => !bound_service_ids.has(Number(service.id)));
    },
  },
  mounted() {
    this.fetch_services();
    this.fetch_service_bindings();
  },

  methods: {
    fetch_services() {
      axios.get(GET_VERIFICATION_SERVICES, get_request_config())
        .then((response) => {
          if (response.data?.status !== 'success') {
            this.show_error(response.data.message);
            return;
          }

          this.services = (Array.isArray(response.data.data) ? response.data.data : [])
            .map((service) => ({
              id: service.id,
              name: service.name ||`Service ${service.id}`,
              description: service.description,
            }))
            .filter((service) => service.id);
        })
        .catch(() => {
          this.show_error('Failed to load verification services.');
      })
    },

    fetch_service_bindings() {
      const entity_config_id = Number(this.entityConfigId);
      if (!Number.isFinite(entity_config_id) || entity_config_id <= 0) {
        this.service_bindings = [];
        return;
      }

      axios.get(GET_VERIFICATION_SERVICE_BINDINGS,
        get_request_config({ params: { entity_config_id } }))
        .then((response) => {
          if (response.data?.status !== 'success') {
            this.service_bindings = [];
            return;
          }

          this.service_bindings = (Array.isArray(response.data.data) ? response.data.data : [])
            .map((binding) => {
              const service_id = Number(binding.service);
              const matched_service = (Array.isArray(this.services) ? this.services : [])
                .find((service) => String(service?.id) === String(service_id));

              return ({
                id: binding.id,
                service_id,
                name: matched_service?.name || binding.service_name,
                description: matched_service?.description || '',
              });
            })
            .filter((binding) => binding.id);
        })
        .catch(() => {
          this.service_bindings = [];
        });
    },

    add_service_binding() {
      if (!this.selected_service_id) {
        this.show_error('Please select a verification service to bind.');
        return;
      }

      const payload = {
        entity_config_id: this.entityConfigId,
        service_id: Number(this.selected_service_id),
      };

      axios.post(BIND_VERIFICATION_SERVICES, payload, get_request_config())
        .then((response) => {
          if (response.data?.status !== 'success') {
            this.show_error(response.data.message);
            return;
          }

          this.selected_service_id = '';
          this.fetch_service_bindings();
          this.show_success(response.data.message);
        })
        .catch(() => {
          this.show_error('Failed to bind verification service.');
        });
    },

    remove_service_binding(binding) {
      const binding_id = Number(binding?.id);
      const service_id = Number(binding?.service_id);
      const entity_config_id = Number(this.entityConfigId);

      if (this.removing_binding_id === binding_id) return;

      this.show_confirm(
        'Remove service binding?',
        'This will unbind the verification service from this entity configuration.',
        () => {
          this.removing_binding_id = binding_id;

          const payload = { entity_config_id, service_id };

          axios.post(REMOVE_VERIFICATION_BINDING, payload, get_request_config())
            .then((response) => {
              if (response.data?.status !== 'success') {
                this.show_error(response.data.message);
                return;
              }

              this.service_bindings = this.service_bindings.filter(
                (existing) => Number(existing?.id) !== binding_id
              );
              this.show_success(response.data.message);
            })
            .catch(() => {
              this.show_error('Failed to remove binding.');
            })
            .finally(() => {
              this.removing_binding_id = null;
            });
        }
      );
    },

    go_to_entity_overview() {
      this.$router.push({
        name: 'EntityConfigDetail',
        params: { entityTypeId: this.entityTypeId },
      });
    },
  },
};
